import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { styled } from "@mui/material/styles";

export type TemplateOwnershipScope = "own" | "all";

const OwnershipToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  alignSelf: "flex-start",
  flexWrap: "wrap",
  maxWidth: "100%",
  "& .MuiToggleButtonGroup-grouped": {
    fontSize: "0.75rem",
    lineHeight: 1.2,
    padding: theme.spacing(0.5, 1),
    textTransform: "none",
  },
}));

type TemplateOwnershipFilterProps = {
  /**
   * What is being filtered, plural and lower case — "team templates", "documents". Both options'
   * accessible names are built from it, so the two read "All accessible team templates" and "Own
   * team templates".
   */
  label: string;
  /**
   * The group's own accessible name, where a surface already has one that is addressed elsewhere.
   *
   * The document selector's group has been "Document ownership filter" since it was written, and
   * the browser suite reaches the conflicts and policies panels through exactly that name. Deriving
   * it from `label` would rename it to "documents ownership filter" and take those locators with it,
   * for no gain a reader would notice.
   */
  groupLabel?: string;
  value: TemplateOwnershipScope;
  onChange: (value: TemplateOwnershipScope) => void;
};

/**
 * Whose templates the panel is showing: the reader's own, or everything they may reach.
 *
 * Two options, not three. An Entity scope sat between them until #5236, asking the reader to decide
 * whose library they wanted before anything had given them a reason to care — and answering with a
 * list All accessible already contains.
 *
 * **All is the default on every panel, and is drawn first.** A panel that opens on Own opens on the
 * narrower answer, so a reader with nothing of their own meets an empty table and no reason to
 * suspect the library is not empty too. Opening on everything they may reach shows them what there
 * is; Own is then a narrowing they choose, which is the direction a filter is supposed to run. Each
 * panel's `useState` holds that default — this control is a controlled component and has no opinion
 * of its own — so the two have to be changed together.
 *
 * One control for every template panel in the app. It lives under `_shared` rather than beside the
 * proposal tabs because the document selector needs it too, and `_shared` may not be reached into
 * from a domain folder in the other direction.
 */
export default function TemplateOwnershipFilter(props: Readonly<TemplateOwnershipFilterProps>) {
  const { groupLabel, label, value, onChange } = props;

  return (
    <OwnershipToggleButtonGroup
      aria-label={groupLabel ?? `${label} ownership filter`}
      color="primary"
      exclusive
      size="small"
      value={value}
      onChange={(_event, nextValue: TemplateOwnershipScope | null) => {
        if (nextValue) onChange(nextValue);
      }}
    >
      <ToggleButton aria-label={`All accessible ${label}`} value="all">All</ToggleButton>
      <ToggleButton aria-label={`Own ${label}`} value="own">Own</ToggleButton>
    </OwnershipToggleButtonGroup>
  );
}
